/**
 * CPV signal inbox (/cpv/signals) — CPV-01 S4. The plant's signals from the evaluation loop: what was raised, against
 * which product, plan and item, by which rule, its severity, status and due date.
 *
 * One read (`GET /bp/cpv/signals/`, services/cpv/inbox.py) with the filters the person picks: product, plan, item,
 * kind, rule, status, severity, and the detection dates (from / to — the person's own range, no age threshold is set
 * here). The status of every row, the counts per status and the words for each code are the server's. A row opens the
 * signal (/cpv/signals/:id), where it is acknowledged and assessed. SHADOW signals are in no inbox (§8.5): the page says
 * only how many the loop stored in SHADOW. When the plant switches ON, each finding stored in SHADOW is raised as a signal
 * with its original detection time and marked "found in shadow" (DD-CPV-50). A signal of a superseded plan that is still
 * open shows the plan version it is carried to (S4-c); the server decides both. Each row carries its episode (D-13): how
 * many hits of the item and rule the signal absorbed while open, with its first and last batch — the server's count.
 * The list is one page of the server's (`page`, `page_size`); every filter, the search (`q`) and the filter choices
 * (`facets`) are the server's, applied before the page is cut, and all of them live in the URL.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Dropdown, Input, Select, Skeleton, Switch, Tooltip } from 'antd';
import { BellRing, CircleDot, EllipsisVertical, EyeOff, RefreshCw, Search, Sparkles } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useListReturn } from '@/components/PageChrome';
import { StatusNotice } from '@/components/genealogy/StatusIndicator';
import { Chip, CpvScreen, DataCard, Pager, SEVERITY_TONE, SIGNAL_STATUS_TONE, StatusText, Table,
  INPUT, pastLastPage, withoutPage, ASSISTANT, KPI } from '@/components/cpv-ui';
import { cpvApi, refusalOf } from './cpvApi';
import { usePlantColumn, withPlantColumn } from './scopeParts';
import { Refusal } from './CpvPage';
import AssistantDrawer from './assistant/AssistantDrawer';
import { summariseSignalRequest } from './assistant/requests';
import useAssistantAllowed from './assistant/useAssistantAllowed';
import PageReturn from '@/components/PageChrome/PageReturn';
import MenuItems from '@/menu-items/index';
import { ACRONYMS } from '@/components/cpv-ui/tokens';
import { InfoHint } from '@/components/cpv-ui';
import './dashboards/Dashboards.css';

/** server status / severity words → indicator states (display only; the label shown is the server's word). */
/** A code made readable: "IN_REVIEW" → "In review"; an abbreviation in ACRONYMS (cpv-ui tokens) stays in capitals ("QC"). */
export const words = (code) => (code ? String(code).replace(/_/g, ' ').split(' ')
  .map((w, i) => (ACRONYMS.has(w.toUpperCase()) ? w.toUpperCase() : i === 0 ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w.toLowerCase()))
  .join(' ') : '');
/** D-13: a signal's episode as the server counts it ({count, first_batch, last_batch}) — words only, nothing counted here. */
export const occurrenceWords = (o) => {
  if (!o || !o.count) return '';
  const n = `${o.count} ${o.count === 1 ? 'occurrence' : 'occurrences'}`;
  return o.count === 1 ? `${n} · ${o.first_batch}` : `${n} · first ${o.first_batch} · last ${o.last_batch}`;
};
const when = (iso) => (iso ? new Date(iso).toLocaleString() : null);
const FILTERS = ['product', 'plan', 'item', 'kind', 'rule', 'status', 'severity', 'detected_from', 'detected_to', 'mine', 'q', 'page', 'page_size'];
/** The parameters that page the list rather than filter it. */
const PAGING = ['page', 'page_size'];
/** Rows per page when the person has not picked a size (the server accepts any positive size up to its maximum). */
const DEFAULT_PAGE_SIZE = 8;

export default function CpvSignals() {
  useListReturn();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [vocab, setVocab] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const filters = useMemo(() => Object.fromEntries(FILTERS.map((k) => [k, params.get(k) || undefined]).filter(([, v]) => v)), [params]);
  const [query, setQuery] = useState(filters.q || '');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [rows, v] = await Promise.all([cpvApi.signals({ page_size: DEFAULT_PAGE_SIZE, ...filters }), vocab ? Promise.resolve(vocab) : cpvApi.signalVocabulary()]);
      setData(rows); setVocab(v);
    } catch (e) {
      if (pastLastPage(e)) { setParams(withoutPage, { replace: true }); setLoading(false); return; }
      setError(refusalOf(e, 'The signals could not be read.'));
    }
    setLoading(false);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const set = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === undefined || value === null || value === '' || value === false) next.delete(key); else next.set(key, String(value));
    if (key !== 'page') next.delete('page');   // a new filter starts at page 1; the page size stays
    setParams(next, { replace: true });
  };
  useEffect(() => {
    const t = setTimeout(() => { if ((filters.q || '') !== query.trim()) set('q', query.trim()); }, INPUT.searchDebounceMs);
    return () => clearTimeout(t);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps
  const rows = data?.results || [];
  const kindWord = (k) => vocab?.kind_words?.[k] || words(k);
  // filter choices are the server's (`facets`: every value the other filters keep, not only this page's) and its closed words
  const facets = data?.facets || {};
  const { products = [], plans = [], items = [], rules = [] } = facets;
  const by = data?.by_status || {};
  const total = Object.values(by).reduce((a, n) => a + (Number(n) || 0), 0);   // the server's per-status counts, summed
  const filtered = Object.keys(filters).some((k) => !PAGING.includes(k));
  const openSignal = (r) => navigate(`/cpv/signals/${r.id}`);
  const [summary, setSummary] = useState(null);          // the row menu's "Summarise signal" (CPV-AI-01), null = closed
  const assistant = useAssistantAllowed();
  const plantCol = usePlantColumn();
  const navigation = MenuItems();

  return (
    <CpvScreen testId="cpv-signals" className="cu-compact">
      {/* one band: the title, then the status strip (bar + legend toggles), the open and shadow figures, then the actions */}
      <header className="cu-card sig-hero" data-testid="cpv-signals-cards" aria-label="Signals by status">
        <div className="sig-hero-title">
          <span className="cu-tile cu-tone--warning" aria-hidden><BellRing size={16} /></span>
          <div>
            <h1 className="cu-title">CPV signals <InfoHint label="About this page" testId="cpv-signals-about">Signals raised by the evaluation loop</InfoHint></h1>
          </div>
        </div>
        {(vocab?.statuses || []).length > 0 && data ? (
          <>
            <div className="sig-hero-status" data-testid="cpv-signals-total">
              <div className="sig-hero-status-head">
                <span><b className="cu-num">{total}</b> {total === 1 ? 'signal' : 'signals'}</span>
                <span className="sig-hero-hint">{filters.status ? <>Showing {words(filters.status).toLowerCase()} · <button type="button" className="cu-link" onClick={() => set('status', undefined)}>show all</button></> : 'Select a status to filter'}</span>
              </div>
              <div className="sig-hero-bar" role="img" data-testid="cpv-signals-bar"
                aria-label={`Signals by status: ${vocab.statuses.map((st) => `${words(st)} ${by[st] ?? 0}`).join(', ')}`}>
                {vocab.statuses.filter((st) => (by[st] ?? 0) > 0).map((st) => (
                  <span key={st} className={`sig-hero-seg cu-tone--${SIGNAL_STATUS_TONE[st] || 'neutral'}${filters.status && filters.status !== st ? ' sig-hero-seg--dim' : ''}`}
                    style={{ flexGrow: by[st] }} title={`${words(st)}: ${by[st]}`} onClick={() => set('status', filters.status === st ? undefined : st)} aria-hidden />
                ))}
              </div>
              <ul className="sig-hero-legend">
                {vocab.statuses.map((st) => (
                  <li key={st}>
                    <button type="button" className={`sig-hero-leg cu-tone--${SIGNAL_STATUS_TONE[st] || 'neutral'}${filters.status === st ? ' sig-hero-leg--on' : ''}`}
                      aria-pressed={filters.status === st} data-testid={`cpv-signals-count-${st}`} onClick={() => set('status', filters.status === st ? undefined : st)}>
                      <span className="sig-hero-dot" aria-hidden />{words(st)}<b className="cu-num">{by[st] ?? 0}</b>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            {'OPEN' in by && (
              <button type="button" className={`sig-hero-stat cu-tone--warning${filters.status === 'OPEN' ? ' sig-hero-stat--on' : ''}`} data-testid="cpv-signals-open"
                aria-pressed={filters.status === 'OPEN'} aria-label={`${words('OPEN')}: ${by.OPEN}, waiting to be assessed. Show only open signals`}
                onClick={() => set('status', filters.status === 'OPEN' ? undefined : 'OPEN')}>
                <span className="sig-hero-stat-icon" aria-hidden><CircleDot size={16} /></span>
                <span><b className="cu-num">{by.OPEN}</b><small>Waiting to be assessed</small></span>
              </button>
            )}
            <div className="sig-hero-stat cu-tone--neutral" data-testid="cpv-signals-shadow" title="Found while the plant runs in SHADOW; in no inbox">
              <span className="sig-hero-stat-icon" aria-hidden><EyeOff size={16} /></span>
              <span><b className="cu-num">{data.shadow ?? 0}</b><small>Stored in shadow mode</small></span>
            </div>
          </>
        ) : <div className="sig-hero-status" />}
        <div className="sig-hero-actions">
          <Tooltip title="Refresh"><Button icon={<RefreshCw size={16} />} onClick={load} loading={loading} aria-label="Refresh" /></Tooltip>
          <PageReturn navigation={navigation} placement="inline" />
        </div>
      </header>
      {error && (
        <section className="cu-card cu-pad"><Refusal refusal={error}><Button size="small" style={{ marginTop: 8 }} onClick={load}>Retry</Button></Refusal></section>
      )}


      <DataCard title="Signals" count={data ? data.count : undefined} subtitle="A row opens the signal, where it is acknowledged and assessed"
        tools={(
          <>
            <Input prefix={<Search size={14} aria-hidden />} placeholder="Search signals…" aria-label="Search signals" style={{ width: 200 }}
              value={query} onChange={(e) => setQuery(e.target.value)} />
            <Select allowClear placeholder="Product" aria-label="Filter by product" style={{ minWidth: 150 }} options={products}
              value={filters.product} onChange={(v) => set('product', v)} />
            <Select allowClear placeholder="Plan" aria-label="Filter by plan" style={{ minWidth: 160 }} options={plans}
              value={filters.plan} onChange={(v) => set('plan', v)} />
            <Select allowClear placeholder="Item" aria-label="Filter by item" style={{ minWidth: 150 }} options={items}
              value={filters.item} onChange={(v) => set('item', v)} />
            <Select allowClear placeholder="Kind" aria-label="Filter by kind" style={{ minWidth: 160 }} value={filters.kind}
              options={(vocab?.kinds || []).map((k) => ({ value: k, label: kindWord(k) }))} onChange={(v) => set('kind', v)} />
            <Select allowClear placeholder="Rule" aria-label="Filter by rule" style={{ minWidth: 100 }} options={rules}
              value={filters.rule} onChange={(v) => set('rule', v)} />
            <Select allowClear placeholder="Severity" aria-label="Filter by severity" style={{ minWidth: 120 }} value={filters.severity}
              options={(vocab?.severities || []).map((s) => ({ value: s, label: words(s) }))} onChange={(v) => set('severity', v)} />
            <DatePicker.RangePicker aria-label="Detected between" allowEmpty={[true, true]}
              onChange={(range) => { const [a, b] = range || []; const next = withoutPage(params);
                if (a) next.set('detected_from', a.format('YYYY-MM-DD')); else next.delete('detected_from');
                if (b) next.set('detected_to', b.format('YYYY-MM-DD')); else next.delete('detected_to');
                setParams(next, { replace: true }); }} />
            <span className="cu-muted cu-nowrap"><Switch size="small" checked={filters.mine === 'true'} aria-label="Only signals routed to me"
              onChange={(on) => set('mine', on ? 'true' : undefined)} /> Routed to me</span>
          </>
        )}
        pager={<Pager data={data} testId="cpv-signals-pager" label="Signal pages" onChange={(page, size) => {
          const next = new URLSearchParams(params);
          if (page > 1) next.set('page', String(page)); else next.delete('page');
          if (size) next.set('page_size', String(size));
          setParams(next, { replace: true });
        }} />}>
        {!error && data === null && <div className="cu-pad"><Skeleton active /></div>}
        {data && !rows.length && (
          <div className="cu-pad">
            <StatusNotice state="neutral" title={filtered ? 'No signal matches these filters.' : (plantCol.show ? 'No signal has been raised on the plants shown.' : 'No signal has been raised on this plant.')} />
            {data.shadow > 0 && <div className="cu-muted" style={{ marginTop: 8 }}>{data.shadow} signal(s) stored in SHADOW mode — not shown in any inbox.</div>}
          </div>
        )}
        {rows.length > 0 && (
          <Table testId="cpv-signals-table" columns={withPlantColumn(plantCol, [{ title: 'Signal' }, { title: 'Severity' }, { title: 'Status' }, { title: 'Product' }, { title: 'Item' },
            { title: 'Batch' }, { title: 'Detected' }, { title: 'Assessment due' }, { title: 'Acknowledged' }, ...(assistant ? [{ title: <span className="cu-sr">Row actions</span> }] : [])])}>
            {rows.map((r) => (
              <tr key={r.id} data-clickable data-testid={`cpv-signal-row-${r.id}`} onClick={() => openSignal(r)}
                className={r.status === 'OPEN' && SEVERITY_TONE[r.severity] === 'error' ? 'cu-row--error' : undefined}>
                <td>
                  <button type="button" className="cu-link" onClick={(e) => { e.stopPropagation(); openSignal(r); }}>
                    {kindWord(r.kind)}{r.rule ? ` · ${r.rule}` : ''}
                  </button>
                  {r.occurrences?.count > 0 && (
                    <div className="cu-cell-meta" data-testid={`cpv-occurrences-${r.id}`}>
                      {r.occurrences.count > 1
                        ? <Chip tone="info" title="Hits of this item and rule recorded against this signal">{occurrenceWords(r.occurrences)}</Chip>
                        : '1 occurrence'}
                    </div>
                  )}
                  {r.found_in_shadow && <div className="cu-cell-meta" data-testid={`cpv-shadow-${r.id}`}>found in shadow</div>}
                </td>
                {plantCol.show && plantCol.cell(r)}
                <td><StatusText tone={SEVERITY_TONE[r.severity] || 'neutral'} label={words(r.severity)} /></td>
                <td><StatusText tone={SIGNAL_STATUS_TONE[r.status] || 'neutral'} label={words(r.status)} /></td>
                <td>{r.product_name}{r.carried_to ? <div className="cu-cell-meta">carried to plan v{r.carried_to.plan_version}</div> : null}</td>
                <td><div className="cu-cell-name" style={{ fontSize: 13 }}>{r.item_label || r.designation_code}</div>
                  {r.item_label ? <div className="cu-cell-meta">{r.designation_code}</div> : null}</td>
                <td className="cu-nowrap">{r.batches.length > 1 ? `${r.first_batch} (+${r.batches.length - 1})` : r.first_batch}</td>
                <td className="cu-nowrap">{when(r.detected_at)}</td>
                <td className="cu-nowrap">{when(r.due_at) || <span className="cpv-notset">no assessment due</span>}</td>
                <td className="cu-nowrap">{r.acknowledged_by_me ? <StatusText tone="ok" label="By you" /> : <span className="cpv-notset">not by you</span>}</td>
                {assistant && (
                <td onClick={(e) => e.stopPropagation()}>
                  <Dropdown trigger={['click']} placement="bottomRight" menu={{ items: [{ key: 'summarise', icon: <Sparkles size={ASSISTANT.icon} aria-hidden />,
                    label: <span data-testid={`cpv-signal-summarise-${r.id}`}>Summarise signal</span>, onClick: () => setSummary(summariseSignalRequest(r.id)) }] }}>
                    <Button type="text" size="small" aria-label="More actions" icon={<EllipsisVertical size={KPI.iconGlyph} aria-hidden />}
                      data-testid={`cpv-signal-menu-${r.id}`} />
                  </Dropdown>
                </td>
                )}
              </tr>
            ))}
          </Table>
        )}
      </DataCard>
      <AssistantDrawer request={summary} onClose={() => setSummary(null)} testId="cpv-signal-summary-drawer" />
    </CpvScreen>
  );
}
